import React, { useState, useEffect } from 'react';
import categoryService from '../services/categoryService';
import { Category } from '../types/contentManagement';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface CategoryManagementProps {
  type: 'policy' | 'library';
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ type }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Partial<Category> | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  useEffect(() => {
    fetchCategories();
  }, [type]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getCategoriesByType(type);
      const sortedData = (data || []).sort((a, b) => a.sort_order - b.sort_order);
      setCategories(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch categories');
      console.error('Error fetching categories:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingCategory({
      name: '',
      type: type,
      color: '#3498db',
      is_active: true,
      sort_order: categories.length + 1
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (category: Category) => {
    setEditingCategory({ ...category });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleSave = async () => {
    if (!editingCategory || !editingCategory.name) {
      setSaveMessage('Error: Category name is required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingCategory.id) {
        await categoryService.updateCategory(editingCategory.id, editingCategory);
        setSaveMessage('Category saved successfully');
      } else {
        await categoryService.createCategory(editingCategory);
        setSaveMessage('Category created successfully');
      }
      
      await fetchCategories();
      handleCloseModal();
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      setSaveMessage('Error saving category');
      console.error('Error saving category:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      setSubmitting(true);
      await categoryService.deleteCategory(categoryToDelete.id);
      await fetchCategories();
      setSaveMessage('Category deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('categoriesUpdated'));
    } catch (err) {
      setSaveMessage('Error deleting category');
      console.error('Error deleting category:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
      setCategoryToDelete(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading categories...</div>;
  }

  return (
    <div className="edit-section">
      <div className="applications-list">
        <div className="list-header">
          <h4>{type === 'policy' ? 'Company Policy' : 'E-Library'} Categories</h4>
          <button 
            className="btn-add" 
            onClick={handleAddClick}
            disabled={submitting}
          >
            <i className="icon-plus"></i>
            Add Category
          </button>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
            border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
            position: 'relative'
          }}>
            {saveMessage}
            <button
              onClick={() => setSaveMessage('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '8px',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close message"
            >
              ×
            </button>
          </div>
        )}

        {error && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}

        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold' }}>Color</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold' }}>Active</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold' }}>Order</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                    No categories found. Click "Add Category" to create one.
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <strong>{category.name}</strong>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '30px', 
                          height: '30px', 
                          backgroundColor: category.color, 
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}></div>
                        <span style={{ fontSize: '12px', color: '#666' }}>{category.color}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: category.is_active ? '#d4edda' : '#f8d7da',
                        color: category.is_active ? '#155724' : '#721c24'
                      }}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {category.sort_order}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleEditClick(category)}
                          disabled={submitting}
                          title="Edit category"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#e3f2fd')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          ✏️
                        </button>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleDeleteClick(category)}
                          disabled={submitting}
                          title="Delete category"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#ffebee')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory?.id ? 'Edit Category' : 'Add New Category'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Category Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={editingCategory?.name || ''}
              onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="Enter category name"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Color <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                value={editingCategory?.color || '#3498db'}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, color: e.target.value }))}
                style={{ width: '60px', height: '40px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={editingCategory?.color || '#3498db'}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, color: e.target.value }))}
                style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="#3498db"
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Sort Order
            </label>
            <input
              type="number"
              value={editingCategory?.sort_order || 1}
              onChange={(e) => setEditingCategory(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              min="1"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="is_active"
              checked={editingCategory?.is_active ?? true}
              onChange={(e) => setEditingCategory(prev => ({ ...prev, is_active: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="is_active" style={{ cursor: 'pointer', fontWeight: '500' }}>Active</label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <button
              onClick={handleCloseModal}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message="Are you sure you want to delete this category? This may affect existing policies/library items using this category."
        itemName={categoryToDelete?.name}
      />
    </div>
  );
};

export default CategoryManagement;
